import { useCallback, useEffect, useRef, useState } from "react"
import {
  cancelAnimation,
  Easing,
  EasingFunction,
  runOnJS,
  SharedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated"

export type OpenState = "open" | "opening" | "closing" | "closed"

/**
 * Helps animate the opening/closing of an element. Takes in the open state as a
 * simple boolean and returns a more granular open state as an enum along with
 * an animated value with a range from 0 (closed) to 1 (open).
 */
export function useOpenAnimation({
  open,
  duration = 500,
  easing = Easing.out(Easing.exp),
  onComplete,
}: {
  open: boolean
  duration?: number
  easing?: EasingFunction
  onComplete?: (open: boolean) => void
}): [
  openAnimation: SharedValue<number>,
  openState: "open" | "opening" | "closing" | "closed",
] {
  const [openState, setOpenState] = useState<
    "open" | "opening" | "closing" | "closed"
  >(open ? "open" : "closed")
  const prevOpen = useRef(open)
  const openAnimation = useSharedValue(open ? 1 : 0)

  const onAnimationComplete = useCallback(
    (open: boolean) => {
      setOpenState(open ? "open" : "closed")
      onComplete?.(open)
    },
    [onComplete],
  )

  useEffect(() => {
    if (open !== prevOpen.current) {
      setOpenState(open ? "opening" : "closing")
      cancelAnimation(openAnimation)
      openAnimation.value = withTiming(
        open ? 1 : 0,
        { duration, easing },
        (complete) => {
          if (complete) {
            runOnJS(onAnimationComplete)(open)
          }
        },
      )
      prevOpen.current = open
    }
  }, [openAnimation, open, duration, easing, onAnimationComplete])

  return [openAnimation, openState]
}
